import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	Container,
	Row,
	Col,
	Button,
	Input,
	Label,
	Spinner,
	Alert
} from "reactstrap";
import { ChromePicker, CirclePicker } from 'react-color';
import { PostData } from '../../../services/FetchData';
import { toast } from "react-toastify";

const Accordion = ({ title, sub, icon, children, defaultOpen = true }) => {
	const [isOpen, setOpen] = React.useState(defaultOpen);
	return (
		<div className="accordion-wrapper">
			<div
				className={`accordion-title ${isOpen ? "open" : ""}`}
				onClick={() => setOpen(!isOpen)}
			>
				<span className="material-icons icon">
					{icon}
				</span>
				<div>
					<h3>{title}</h3>
					<p>{sub}</p>
				</div>
				<span className="material-icons more">
					expand_more
				</span>
			</div>
			<div className={`accordion-item ${!isOpen ? "collapsed" : ""}`}>
				<div className="accordion-content">{children}</div>
			</div>
		</div>
	);
};

const CmsAssistantMessengerSettings = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [saveLoading, setSaveLoading] = useState(false);
	const [errorMessages, setErrorMessages] = useState("");
	const [showBgColorPicker, setShowBgColorPicker] = useState(false);
	const [showBtnColorPicker, setShowBtnColorPicker] = useState(false);

	// Get website data from location state
	const websiteId = location.state?.websiteId || "";
	const websiteURL = location.state?.websiteURL || "";
	const widgetApiKey = location.state?.widgetApiKey || "";

	// Default images from rvecfe
	const defaultLogo = "https://getuikit.com/docs/images/avatar.jpg";
	const defaultAvatar = "https://getuikit.com/docs/images/avatar.jpg";

	// Messenger settings state
	const [settings, setSettings] = useState({
		background: '#5c50e6',
		btnColor: '#5c50e6',
		textColour: 'white',
		btnFontColor: 'white',
		image: defaultLogo,
		avatar: defaultAvatar,
		botName: 'ARVEE',
		botMessage: 'Hi there! How can I help you today?',
		welcomeTitle: 'Hello! 👋',
		welcomeDescription: 'We are here to help. Ask us anything!',
		position: 'Right',
		rightSpace: '30px',
		bottomSpace: '30px'
	});

	useEffect(() => {
		if (!websiteId) {
			navigate("/getStarted/cmsAssistant", { replace: true });
		}
	}, [websiteId, navigate]);

	const handleColorChange = (field, color) => {
		const brightness = Math.round(((parseInt(color.rgb.r) * 299) +
			(parseInt(color.rgb.g) * 587) +
			(parseInt(color.rgb.b) * 114)) / 1000);
		const textColor = (brightness > 125) ? 'black' : 'white';

		if (field === 'background') {
			setSettings(prev => ({ ...prev, background: color.hex, textColour: textColor }));
		} else if (field === 'btnColor') {
			setSettings(prev => ({ ...prev, btnColor: color.hex, btnFontColor: textColor }));
		}
	};

	const handleInputChange = (field, value) => {
		setSettings(prev => ({ ...prev, [field]: value }));
	};

	const handleImageUpload = (e, field) => {
		let files = e.target.files || e.dataTransfer.files;
		if (!files.length) return;

		let reader = new FileReader();
		reader.onload = (e) => {
			setSettings(prev => ({ ...prev, [field]: e.target.result }));
		};
		reader.readAsDataURL(files[0]);
	};

	const saveSettings = () => {
		setSaveLoading(true);
		setErrorMessages("");

		const requestData = {
			websiteId: websiteId,
			messengerSettings: {
				Bot_Agent_Background_Color: settings.background,
				Bot_Agent_Button_Color: settings.btnColor,
				Bot_Agent_Background_Text_Color: settings.textColour,
				Bot_Agent_Button_Text_Color: settings.btnFontColor,
				Header_Logo: settings.image,
				Bot_Agent_Avatar: settings.avatar,
				Agent_Bot_Name: settings.botName,
				Bot_Agent_Description: settings.botMessage,
				Welcome_Title: settings.welcomeTitle,
				Welcome_Description: settings.welcomeDescription,
				Bot_Agent_Position: settings.position,
				Bot_Agent_Right_Spacing: settings.rightSpace,
				Bot_Agent_Left_Spacing: settings.bottomSpace
			}
		};

		// Use ms4 for CMS Assistant API
		PostData("ms4", "api/v1/cmsAssistant/saveMessengerSettings", requestData).then(result => {
			if (result !== "Invalid" && result !== undefined) {
				if (result.status === "success") {
					toast.success("Messenger settings saved successfully!");
					// Navigate to completion page with API key
					navigate("/getStarted/cmsAssistantComplete", { 
						state: { 
							websiteId: websiteId,
							websiteURL: websiteURL,
							widgetApiKey: widgetApiKey
						},
						replace: true 
					});
				} else {
					setErrorMessages(result.message || "Failed to save settings");
				}
			}
			setSaveLoading(false);
		}).catch(error => {
			setErrorMessages("An error occurred. Please try again.");
			setSaveLoading(false);
		});
	};

	return (
		<>
			<section className='choosePlatformHeader'>
				<div className='container'>
					<Row className="align-items-center justify-content-center">
						<Col xs={"auto"}>
							<h2 className='mt-5'>Configure Messenger</h2>
							<p className='text-center text-muted'>Step 2 of 2: Customize your messenger appearance</p>
						</Col>
					</Row>
				</div>
			</section>

			<Container className="pb-5 mt-4 messenger">
				{errorMessages && (
					<Row className="justify-content-center mb-3">
						<Col lg={10}>
							<Alert color="danger">{errorMessages}</Alert>
						</Col>
					</Row>
				)}
				<Row>
					<Col lg="7">
						{/* Style Accordion */}
						<Accordion title="Style your Messenger" sub="Customize colors and branding" icon="design_services">
							<div className='chat-editor-wrap'>
								{/* Header Logo */}
								<div className="form-group mb-4">
									<Label>Header Logo</Label>
									<div className="d-flex align-items-center gap-3">
										<div className="logo-preview" style={{ 
											width: 60, 
											height: 60, 
											borderRadius: 8,
											background: '#f5f5f5', 
											display: 'flex', 
											alignItems: 'center', 
											justifyContent: 'center',
											overflow: 'hidden',
											border: '1px solid #ddd'
										}}>
											{settings.image ? (
												<img src={settings.image} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
											) : (
												<span className="material-icons" style={{ color: '#999' }}>image</span>
											)}
										</div>
										<Input
											type="file"
											accept="image/*"
											onChange={(e) => handleImageUpload(e, "image")}
											style={{ maxWidth: 250 }}
										/>
									</div>
								</div>

								{/* Avatar */}
								<div className="form-group mb-4">
									<Label>Avatar</Label>
									<div className="d-flex align-items-center gap-3">
										<div className="avatar-preview" style={{ 
											width: 50, 
											height: 50, 
											borderRadius: '50%',
											background: '#f5f5f5', 
											display: 'flex', 
											alignItems: 'center', 
											justifyContent: 'center',
											overflow: 'hidden',
											border: '1px solid #ddd'
										}}>
											{settings.avatar ? (
												<img src={settings.avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
											) : (
												<span className="material-icons" style={{ color: '#999', fontSize: 20 }}>person</span>
											)}
										</div>
										<Input
											type="file"
											accept="image/*"
											onChange={(e) => handleImageUpload(e, "avatar")}
											style={{ maxWidth: 250 }}
										/>
									</div>
								</div>

								{/* Colors */}
								<Row>
									<Col md="6">
										<div className="form-group mb-4">
											<Label>Background Color</Label>
											<div 
												className="color-input d-flex align-items-center" 
												style={{ 
													border: '1px solid #ddd', 
													borderRadius: 4, 
													padding: '8px 12px',
													cursor: 'pointer',
													background: '#fff'
												}}
												onClick={() => setShowBgColorPicker(!showBgColorPicker)}
											>
												<span style={{ flex: 1, color: '#666' }}>{settings.background}</span>
												<div style={{ 
													width: 24, 
													height: 24, 
													background: settings.background, 
													borderRadius: 4,
													border: '1px solid rgba(0,0,0,0.1)'
												}}></div>
											</div>
											<div className="mt-2">
												<CirclePicker
													color={settings.background}
													onChange={(color) => handleColorChange('background', color)}
													width="100%"
												/>
											</div>
										</div>
									</Col>
									<Col md="6">
										<div className="form-group mb-4">
											<Label>Button Color</Label>
											<div 
												className="color-input d-flex align-items-center" 
												style={{ 
													border: '1px solid #ddd', 
													borderRadius: 4, 
													padding: '8px 12px',
													cursor: 'pointer',
													background: '#fff'
												}}
												onClick={() => setShowBtnColorPicker(!showBtnColorPicker)}
											>
												<span style={{ flex: 1, color: '#666' }}>{settings.btnColor}</span>
												<div style={{ 
													width: 24, 
													height: 24, 
													background: settings.btnColor, 
													borderRadius: 4,
													border: '1px solid rgba(0,0,0,0.1)'
												}}></div>
											</div>
											<div className="mt-2">
												<CirclePicker
													color={settings.btnColor}
													onChange={(color) => handleColorChange('btnColor', color)}
													width="100%"
												/>
											</div>
										</div>
									</Col>
								</Row>
							</div>
						</Accordion>

						{/* Trigger Message Accordion */}
						<Accordion title="Set your trigger message" sub="First message users see" icon="waving_hand">
							<div className='chat-editor-wrap'>
								<div className="form-group">
									<Label>Trigger Message</Label>
									<Input
										type="textarea"
										rows={3}
										value={settings.botMessage}
										onChange={(e) => handleInputChange('botMessage', e.target.value)}
									/>
								</div>
							</div>
						</Accordion>

						{/* Welcome Message Accordion */}
						<Accordion title="Set your welcome message" sub="Introduce your team" icon="sentiment_very_satisfied">
							<div className='chat-editor-wrap'>
								<div className="form-group mb-3">
									<Label>Greeting Title</Label>
									<Input
										type="text"
										value={settings.welcomeTitle}
										onChange={(e) => handleInputChange('welcomeTitle', e.target.value)}
									/>
								</div>
								<div className="form-group">
									<Label>Team Intro</Label>
									<Input
										type="textarea"
										rows={2}
										value={settings.welcomeDescription}
										onChange={(e) => handleInputChange('welcomeDescription', e.target.value)}
									/>
								</div>
							</div>
						</Accordion>

						{/* Launcher Settings Accordion */}
						<Accordion title="Launcher settings" sub="Position and spacing" icon="rocket_launch">
							<div className='chat-editor-wrap'>
								<div className="form-group mb-3">
									<Label>Launcher Position</Label>
									<Input
										type="select"
										value={settings.position}
										onChange={(e) => handleInputChange('position', e.target.value)}
									>
										<option value="Right">Right</option>
										<option value="Left">Left</option>
									</Input>
								</div>
								<Row>
									<Col md="6">
										<div className="form-group">
											<Label>Side Spacing</Label>
											<Input
												type="text"
												value={settings.rightSpace}
												placeholder="30px"
												onChange={(e) => handleInputChange('rightSpace', e.target.value)}
											/>
										</div>
									</Col>
									<Col md="6">
										<div className="form-group">
											<Label>Bottom Spacing</Label>
											<Input
												type="text"
												value={settings.bottomSpace}
												placeholder="30px"
												onChange={(e) => handleInputChange('bottomSpace', e.target.value)}
											/>
										</div>
									</Col>
								</Row>
							</div>
						</Accordion>

						{/* Bot Settings Accordion */}
						<Accordion title="Bot settings" sub="Name your assistant" icon="settings_suggest">
							<div className='chat-editor-wrap'>
								<div className="form-group">
									<Label>Bot Name</Label>
									<Input
										type="text"
										value={settings.botName}
										onChange={(e) => handleInputChange('botName', e.target.value)}
									/>
								</div>
							</div>
						</Accordion>

						{/* Save Button */}
						<div className="text-center mt-4">
							{saveLoading ? (
								<Button className='cta' color="primary" type="button" disabled style={{ minWidth: '250px' }}>
									<Spinner className="white mr-1" size="sm" color="light" />
									<span className="ml-2 d-inline-block">Saving...</span>
								</Button>
							) : (
								<Button className='cta' color="primary" onClick={saveSettings} style={{ minWidth: '250px' }}>
									Save & Continue to Dashboard
								</Button>
							)}
						</div>
					</Col>

					{/* Preview Column */}
					<Col lg="5">
						<div style={{ position: 'sticky', top: '100px' }}>
							<h5 className="mb-3 text-center text-muted">Preview</h5>
							<div id="rvContainer">
								<div className="rvMessenger">
									<div className="rvMsgHead" style={{ color: settings.textColour, backgroundColor: settings.background }}>
										<div className="rvMsgHeaderView">
											<div className="rvMsgHeaderOpacity">
												<div className="rvMsgWrapper">
													{/* Logo */}
													<div className="rvMsgImgWrap">
														{settings.image ? (
															<img src={settings.image} alt="Logo" style={{ width: 40, height: 40, objectFit: 'contain' }} />
														) : (
															<div style={{ 
																width: 40, 
																height: 40, 
																background: 'rgba(255,255,255,0.2)', 
																borderRadius: 8,
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center'
															}}>
																<span className="material-icons" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 24 }}>business</span>
															</div>
														)}
													</div>
													<div className="rvMsgContWrap">
														<div className="title">{settings.welcomeTitle}</div>
														<div className="paraContent" style={{ color: settings.textColour }}>{settings.welcomeDescription}</div>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div className="rvHomeScreenWrap">
										<div className="rvHomeScreen">
											<div className="rvHomeScreenInnerWrap">
												<div className="rvHomeScreenCard">
													{/* Avatar */}
													<div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
														<div style={{ 
															width: 36, 
															height: 36, 
															borderRadius: '50%', 
															background: settings.avatar ? 'transparent' : '#e0e0e0',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															overflow: 'hidden',
															marginRight: 10
														}}>
															{settings.avatar ? (
																<img src={settings.avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
															) : (
																<span className="material-icons" style={{ color: '#999', fontSize: 20 }}>person</span>
															)}
														</div>
														<div>
															<div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{settings.botName}</div>
															<div style={{ fontSize: 11, color: '#888' }}>Online</div>
														</div>
													</div>
													<div className="rv-messenger-card-text-header">
														Start a conversation
													</div>
													<button
														className="rvBtnFullWidth"
														style={{ backgroundColor: settings.btnColor, color: settings.btnFontColor }}
													>
														Ask {settings.botName}
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className='launcher' style={{ color: settings.textColour, backgroundColor: settings.background }}>
									<span className="material-icons">close</span>
								</div>
							</div>
						</div>
					</Col>
				</Row>
			</Container>

			{/* Background Color Picker Modal */}
			{showBgColorPicker && (
				<div 
					style={{ 
						position: 'fixed', 
						top: 0, 
						left: 0, 
						right: 0, 
						bottom: 0, 
						backgroundColor: 'rgba(0,0,0,0.3)',
						zIndex: 9999,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center'
					}}
					onClick={() => setShowBgColorPicker(false)}
				>
					<div 
						style={{ background: '#fff', padding: 20, borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}
						onClick={(e) => e.stopPropagation()}
					>
						<h6 className="mb-3">Background Color</h6>
						<ChromePicker
							color={settings.background}
							onChange={(color) => handleColorChange('background', color)}
						/>
						<div className="text-center mt-3">
							<Button color="primary" size="sm" onClick={() => setShowBgColorPicker(false)}>
								Done
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* Button Color Picker Modal */}
			{showBtnColorPicker && (
				<div 
					style={{ 
						position: 'fixed', 
						top: 0, 
						left: 0, 
						right: 0, 
						bottom: 0, 
						backgroundColor: 'rgba(0,0,0,0.3)',
						zIndex: 9999,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center'
					}}
					onClick={() => setShowBtnColorPicker(false)}
				>
					<div 
						style={{ background: '#fff', padding: 20, borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}
						onClick={(e) => e.stopPropagation()}
					>
						<h6 className="mb-3">Button Color</h6>
						<ChromePicker
							color={settings.btnColor}
							onChange={(color) => handleColorChange('btnColor', color)}
						/>
						<div className="text-center mt-3">
							<Button color="primary" size="sm" onClick={() => setShowBtnColorPicker(false)}>
								Done
							</Button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default CmsAssistantMessengerSettings;
